<template>
  <!-- Scroll story (S2_Story): words scroll on one side while the photo
       stays put and changes with each step. On phones (section.mobile):
       "full" keeps the photo filling the screen with each step on a card over it,
       "pile" makes each step a card that sticks and the next slides over it.
       A photo much wider than the frame shows whole, over a blurred copy of itself. -->
  <SectionBand :heading="section.heading || section.title" :variant="variant" :background="section.background" label="Our story">
    <div class="s2-story" :data-mobile="mobile">
      <ol class="s2-story__steps">
        <li v-for="(s, i) in steps" :key="i" ref="stepEls" :data-i="i" class="s2-story__step" :class="{on: i === active}" :style="{'--i': i}">
          <Media v-if="s.image" :media="s.image" ratio="4 / 3" class="s2-story__inline" />
          <div class="s2-story__words">
            <span class="t-eyebrow">{{ i + 1 }}</span>
            <h3 class="t-h2">{{ s.title }}</h3>
            <div class="s2-prose t-body" v-html="html(s.text)" />
          </div>
        </li>
      </ol>
      <div class="s2-story__stage" aria-hidden="true">
        <div ref="frameEl" class="s2-story__frame" :data-fx="fx" :data-dir="dir" @load.capture="measure" @loadedmetadata.capture="measure">
          <div v-for="(s, i) in steps" :key="i" :data-i="i" class="s2-story__layer" :class="{on: i === active, off: i === leaving, wide: isWide(i)}">
            <div v-if="isWide(i) && backdrop(s.image)" class="s2-story__fill" :style="{backgroundImage: `url(&quot;${backdrop(s.image)}&quot;)`}" />
            <Media :media="s.image" ratio="4 / 5" class="s2-story__img" />
          </div>
        </div>
      </div>
    </div>
  </SectionBand>
</template>

<script setup>
import {computed, onBeforeUnmount, onMounted, ref, watch} from "vue";
import SectionBand from "../shared/SectionBand.vue";
import Media from "../shared/Media.vue";
import {contentHtml} from "@/utils/contentHtml.js";
import {mediaOf} from "@/utils/blocks.js";
import {sizedUrl} from "@/utils/photoSizes.js";

const props = defineProps({section: {type: Object, required: true}, variant: {type: [Number, String], default: 1}});
const steps = computed(() => (Array.isArray(props.section.steps) ? props.section.steps : []).filter((s) => s && (s.title || s.text)).slice(0, 8));
const active = ref(0);
// The photo change (section.transition): the leaving photo keeps its "off" look until the new one has landed.
const FX = ["crossfade", "dip", "slide", "wipe", "circle", "zoom", "blur", "doors", "deck", "push"];
const fx = computed(() => (FX.includes(props.section.transition) ? props.section.transition : "crossfade"));
const mobile = computed(() => (props.section.mobile === "pile" ? "pile" : "full"));
const leaving = ref(-1);
const dir = ref("next");
let leaveTimer = 0;
watch(active, (now, was) => {
  dir.value = now < was ? "back" : "next";
  leaving.value = was;
  clearTimeout(leaveTimer);
  leaveTimer = setTimeout(() => { leaving.value = -1; }, 1500);
});
// Wide photos (1 Nov): a photo more than 1.5x the frame's shape would be cut to a strip, so it shows whole
// instead, over a blurred copy of itself. Measured from the loaded photo; until then it fills the frame.
const ratios = ref({});
const frameRatio = ref(0.8);
const frameEl = ref(null);
const isWide = (i) => ratios.value[i] > frameRatio.value * 1.5;
// The blurred copy only needs the smallest size (never the original, which can be several MB).
const backdrop = (media) => { const m = mediaOf(media); return m.kind === "image" ? sizedUrl(m.src, 400) : m.poster; };
function measure(e) {
  const el = e?.target || e;
  const w = el.naturalWidth || el.videoWidth, h = el.naturalHeight || el.videoHeight;
  const i = Number(el.closest?.(".s2-story__layer")?.dataset.i);
  if (w && h && !Number.isNaN(i)) ratios.value = {...ratios.value, [i]: w / h};
}
function sizeFrame() {
  const f = frameEl.value;
  if (f?.clientHeight) frameRatio.value = f.clientWidth / f.clientHeight;
}
const stepEls = ref([]);
const html = (t) => contentHtml(t || "");
// The step nearest the middle of the visible area is the active one. Measured on scroll (any scroller, captured)
// rather than with an IntersectionObserver, which left every step greyed out in the studio preview (1 Nov).
let frame = 0;
function pick() {
  frame = 0;
  const mid = window.innerHeight / 2;
  let best = 0, bestGap = Infinity;
  stepEls.value.forEach((el, i) => {
    if (!el) return;
    const r = el.getBoundingClientRect();
    const gap = Math.abs((r.top + r.bottom) / 2 - mid);
    if (gap < bestGap) { best = Number(el.dataset.i) || i; bestGap = gap; }
  });
  active.value = best;
  if (mobile.value === "pile" && phone?.matches) stepBack();
}
// Card pile: the card underneath steps back as the next one slides over it.
let phone = null;
function stepBack() {
  const els = stepEls.value.filter(Boolean);
  els.forEach((el, i) => {
    const next = els[i + 1];
    const p = next ? Math.min(1, Math.max(0, 1 - (next.getBoundingClientRect().top - el.getBoundingClientRect().top) / el.offsetHeight)) : 0;
    el.style.transform = p ? `scale(${1 - p * 0.07})` : "";
    el.style.filter = p ? `brightness(${1 - p * 0.25})` : "";
  });
}
const onScroll = () => { if (!frame) frame = requestAnimationFrame(pick); };
const onResize = () => { sizeFrame(); onScroll(); };
onMounted(() => {
  phone = window.matchMedia("(max-width: 760px)");
  window.addEventListener("scroll", onScroll, {passive: true, capture: true});
  window.addEventListener("resize", onResize, {passive: true});
  sizeFrame();
  // Photos that loaded before the page came alive never fire load here.
  frameEl.value?.querySelectorAll("img, video").forEach((el) => { if (el.complete || el.readyState > 0) measure(el); });
  pick();
});
onBeforeUnmount(() => {
  window.removeEventListener("scroll", onScroll, {capture: true});
  window.removeEventListener("resize", onResize);
  if (frame) cancelAnimationFrame(frame);
  clearTimeout(leaveTimer);
});
</script>

<style scoped>
.s2-story { display: grid; grid-template-columns: 1fr 1fr; gap: clamp(2rem, 5vw, 4rem); }
.s2-story__steps { list-style: none; margin: 0; padding: 0; }
.s2-story__step { min-height: min(60vh, 520px); display: flex; flex-direction: column; justify-content: center; opacity: .45; transition: opacity .4s; }
.s2-story__step.on { opacity: 1; }
.s2-story__step .t-h2 { margin: .3rem 0 .6rem; }
.s2-story__stage { position: relative; }
.s2-story__frame { position: sticky; top: 15vh; aspect-ratio: 4 / 5; overflow: hidden; isolation: isolate; border-radius: var(--t-radius, 0); }
.s2-story__layer { position: absolute; inset: 0; }
.s2-story__layer :deep(.s2-media) { margin: 0; height: 100%; }
.s2-story__layer :deep(.s2-media__el) { height: 100% !important; }
.s2-story__layer.wide { background: #111; }
.s2-story__fill { position: absolute; inset: -40px; background: center / cover no-repeat; filter: blur(28px) brightness(.8); transform: scale(1.1); }
.s2-story__layer.wide :deep(.s2-media) { position: relative; }
.s2-story__layer.wide :deep(.s2-media__el) { object-fit: contain !important; }
.s2-story__layer.on { z-index: 3; }
.s2-story__layer.off { z-index: 2; }
/* Waiting photos sit hidden at their start look, with no animation, so the next change starts clean. */
.s2-story__layer:not(.on):not(.off) { z-index: 1; visibility: hidden; transition: none !important; }

[data-fx="crossfade"] .s2-story__layer { opacity: 0; transition: opacity .9s ease; }
[data-fx="crossfade"] .on, [data-fx="crossfade"] .off { opacity: 1; }
[data-fx="dip"] { background: var(--t-bg, transparent); }
[data-fx="dip"] .s2-story__layer { opacity: 0; transition: opacity .4s ease .4s; }
[data-fx="dip"] .on { opacity: 1; }
[data-fx="dip"] .off { opacity: 0; transition-delay: 0s; }
[data-fx="slide"] .s2-story__layer { transform: translateY(100%); transition: transform .85s cubic-bezier(.65,0,.35,1), filter .85s; }
[data-fx="slide"][data-dir="back"] .s2-story__layer { transform: translateY(-100%); }
[data-fx="slide"] .s2-story__layer.on { transform: none; }
[data-fx="slide"] .s2-story__layer.off { transform: translateY(-28%); filter: brightness(.55); }
[data-fx="slide"][data-dir="back"] .s2-story__layer.off { transform: translateY(28%); }
[data-fx="wipe"] .s2-story__layer { clip-path: inset(0 100% 0 0); transition: clip-path .9s cubic-bezier(.77,0,.18,1); }
[data-fx="wipe"][data-dir="back"] .s2-story__layer { clip-path: inset(0 0 0 100%); }
[data-fx="wipe"] .s2-story__layer.on, [data-fx="wipe"] .s2-story__layer.off { clip-path: inset(0); }
[data-fx="circle"] .s2-story__layer { clip-path: circle(0% at 50% 55%); transition: clip-path 1s cubic-bezier(.65,0,.35,1); }
[data-fx="circle"] .on, [data-fx="circle"] .off { clip-path: circle(75% at 50% 55%); }
[data-fx="zoom"] .s2-story__layer { opacity: 0; transform: scale(1.14); transition: opacity .9s ease, transform 1.4s cubic-bezier(.22,1,.36,1); }
[data-fx="zoom"] .on { opacity: 1; transform: none; }
[data-fx="zoom"] .off { opacity: 1; transform: scale(.97); }
[data-fx="blur"] .s2-story__layer { opacity: 0; filter: blur(18px); transform: scale(1.04); transition: opacity .8s ease, filter .8s ease, transform .8s ease; }
[data-fx="blur"] .on { opacity: 1; filter: none; transform: none; }
[data-fx="blur"] .off { opacity: 0; filter: blur(18px); }
[data-fx="doors"] .s2-story__layer { clip-path: inset(0 50% 0 50%); transition: clip-path .9s cubic-bezier(.77,0,.18,1); }
[data-fx="doors"] .on, [data-fx="doors"] .off { clip-path: inset(0); }
[data-fx="deck"] { overflow: visible; }
[data-fx="deck"] .s2-story__layer { border-radius: var(--t-radius, 0); overflow: hidden; box-shadow: 0 18px 40px rgba(0,0,0,.25); transform: translateY(105%) rotate(5deg); opacity: 0; transition: transform .9s cubic-bezier(.22,1,.36,1), opacity .5s ease, filter .9s; }
[data-fx="deck"] .s2-story__layer.on { transform: none; opacity: 1; }
[data-fx="deck"] .s2-story__layer.off { transform: scale(.88) translateY(-5%); opacity: 1; filter: brightness(.6); }
[data-fx="push"] .s2-story__layer { transform: translateX(100%); transition: transform .85s cubic-bezier(.65,0,.35,1), filter .85s; }
[data-fx="push"][data-dir="back"] .s2-story__layer { transform: translateX(-100%); }
[data-fx="push"] .s2-story__layer.on { transform: none; }
[data-fx="push"] .s2-story__layer.off { transform: translateX(-35%); filter: brightness(.6); }
[data-fx="push"][data-dir="back"] .s2-story__layer.off { transform: translateX(35%); }
.s2-story__inline { display: none; margin-bottom: 1rem; }
@media (prefers-reduced-motion: reduce) {
  .s2-story__step { transition: none; }
  .s2-story__frame .s2-story__layer { transition: opacity .3s ease !important; transform: none !important; filter: none !important; clip-path: none !important; opacity: 0; }
  .s2-story__frame .s2-story__layer.on { opacity: 1; }
}
@media (max-width: 760px) {
  .s2-story { grid-template-columns: 1fr; }
  .s2-story__step { opacity: 1; }

  /* Full screen: the photo stage fills the screen behind, each step rides over it on a card. */
  [data-mobile="full"] > * { grid-area: 1 / 1; }
  [data-mobile="full"] .s2-story__stage { z-index: 0; }
  [data-mobile="full"] .s2-story__frame { top: 0; height: 100svh; aspect-ratio: auto; border-radius: var(--t-radius, 0); }
  [data-mobile="full"] .s2-story__frame::after { content: ""; position: absolute; inset: 0; z-index: 4; background: linear-gradient(transparent 45%, rgba(0,0,0,.4)); pointer-events: none; }
  [data-mobile="full"] .s2-story__steps { position: relative; z-index: 1; }
  [data-mobile="full"] .s2-story__step { min-height: 100svh; justify-content: flex-end; padding: 0 .9rem 1.6rem; }
  [data-mobile="full"] .s2-story__step:first-child { min-height: 115svh; }
  [data-mobile="full"] .s2-story__words { background: color-mix(in srgb, var(--t-surface, #fff) 88%, transparent); color: var(--t-ink, #111); backdrop-filter: blur(10px); -webkit-backdrop-filter: blur(10px); border-radius: calc(var(--t-radius, 12px) + 4px); padding: 1rem 1.1rem 1.2rem; box-shadow: 0 10px 30px rgba(0,0,0,.2); }

  /* Card pile: each step is a card that sticks below the top bar; the next one slides over it. */
  [data-mobile="pile"] .s2-story__stage { display: none; }
  [data-mobile="pile"] .s2-story__inline { display: block; margin: 0; }
  [data-mobile="pile"] .s2-story__step { position: sticky; top: calc(5.5rem + var(--i) * .9rem); min-height: 0; justify-content: flex-start; margin-bottom: 1.6rem; background: var(--t-surface, #fff); color: var(--t-ink, #111); border-radius: calc(var(--t-radius, 12px) + 6px); overflow: hidden; box-shadow: 0 -6px 24px rgba(0,0,0,.12); transform-origin: 50% 0; will-change: transform; }
  [data-mobile="pile"] .s2-story__words { padding: 1rem 1.1rem 1.3rem; }
}
</style>
